import { Link } from "react-router";
import type { LeadListItem } from "@shared-core/data/api-types";
import { LEAD_STAGES, LEAD_STAGE_LABELS } from "@shared-core/domain/leads";
import { paths } from "@shared-core/routing/routes";
import { formatCount } from "@shared-core/domain/formatters";
import { Badge } from "../../primitives/badge";

// A lead's stage changes on its detail page, not by dragging cards here —
// see the product brief's frontend-considerations note: click-through gets
// most of the daily-glance value of a kanban board without the extra
// drag-and-drop engineering cost.
export function LeadKanbanBoard({ leads }: { leads: LeadListItem[] }) {
  const byStage = new Map<string, LeadListItem[]>();
  for (const stage of LEAD_STAGES) byStage.set(stage, []);
  for (const lead of leads) {
    byStage.get(lead.stage)?.push(lead);
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {LEAD_STAGES.map((stage) => {
        const stageLeads = byStage.get(stage) ?? [];
        return (
          <div
            key={stage}
            className="flex-1 min-w-[220px] max-w-[280px] shrink-0"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <h3 className="text-sm font-medium text-foreground">
                {LEAD_STAGE_LABELS[stage]}
              </h3>
              <span className="text-xs text-muted-foreground">
                {formatCount(stageLeads.length)}
              </span>
            </div>
            <div className="space-y-2 min-h-[80px] rounded-lg bg-surface/50 border border-border p-2">
              {stageLeads.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-4">
                  No leads
                </div>
              ) : (
                stageLeads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={paths.leadDetail({ leadId: lead.id })}
                    className="block bg-background border border-border rounded-md p-3 hover:border-primary transition-colors"
                  >
                    <div className="text-sm font-medium text-foreground mb-1">
                      {lead.company_name}
                    </div>
                    {lead.has_pending_match && (
                      <Badge variant="warning" className="text-[10px] mt-1">
                        Match to review
                      </Badge>
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
